import layoutNames from '@/modules/public/search/components/SearchFilters/components/FiltersTabs/People/layoutNames.json';
import pricingMethods from '@/modules/public/search/components/SearchFilters/components/FiltersTabs/Price/pricingMethods.json';
import searchOptions from '@/modules/public/search/store/options.json';
import { getTimeLabel } from '@/services/hours-map';

export default function getSearchLabelledFiltersList(state, getters) {
  const arr = [];

  const isFilterReduced = (field, key = null) => {
    return state.reducedFilters?.some(filter => {
      if (key) {
        return filter.field === field && (filter.key === key || filter.keys?.includes(key));
      }
      return filter.field === field;
    });
  };

  const getFilterReducedProperties = (field, key = null) => {
    const reducedFilter = isFilterReduced(field, key);

    if (!reducedFilter) {
      return { disabled: false };
    }

    const obj = {};
    obj.disabled = true;
    if (reducedFilter.label) {
      obj.label = reducedFilter.label;
    }
    return obj;
  };

  if (state.filters.people.value) {
    arr.push({ label: `${state.filters.people.value} people`, disabled: false });
  }

  const dateFilter = state.filters.date.value;
  const timeFromFilter = state.filters.timeFrom.value;
  const timeToFilter = state.filters.timeTo.value;
  const timeFormat = state.l10n.timeFormat;
  if (dateFilter) {
    arr.push({
      label: dateFilter,
      ...getFilterReducedProperties('date'),
    });
  }
  if (timeFromFilter || timeToFilter) {
    arr.push({
      label: `${getTimeLabel(timeFromFilter, timeFormat)} - ${getTimeLabel(timeToFilter, timeFormat)}`,
      ...getFilterReducedProperties('timeRange'),
    });
  }

  // Price method and range
  if (state.filters.priceMethod.value) {
    const pricingMethod = pricingMethods.find(a => a.id === state.filters.priceMethod.value);
    if (pricingMethod) {
      arr.push({ label: pricingMethod.label, ...getFilterReducedProperties('priceMethod') });
    }
  }
  if (!getters.isPriceFilterDefault) {
    const currencySign = state.l10n.currencySign;

    if (getters.isMinPriceFilterDefault && !getters.isMaxPriceFilterDefault) {
      arr.push({
        label: `Up to ${currencySign}${state.filters.priceRange.value.to}`,
        ...getFilterReducedProperties('priceRange'),
      });
    } else {
      arr.push({
        label: `${currencySign}${state.filters.priceRange.value.from} - ${currencySign}${state.filters.priceRange.value.to}`,
        ...getFilterReducedProperties('priceRange'),
      });
    }
  }

  // Layout
  if (state.filters.layout.value) {
    arr.push({ label: `${layoutNames[state.filters.layout.value]} layout`, ...getFilterReducedProperties('layout') });
  }

  // Catering options
  if (state.filters.catering.value?.length) {
    if (getFilterReducedProperties('catering')?.disabled) {
      state.filters.catering.value.forEach(value => {
        const option = searchOptions.cateringOptions.find(opt => parseInt(opt.id, 10) === parseInt(value, 10));
        if (option?.label) {
          arr.push({ label: option.label, disabled: true });
        }
      });
    } else {
      state.filters.catering.value.forEach(value => {
        const option = searchOptions.cateringOptions.find(opt => parseInt(opt.id, 10) === parseInt(value, 10));
        if (option?.label) {
          arr.push({ label: option.label, ...getFilterReducedProperties('catering', value.toString()) });
        }
      });
    }
  }

  // Room features
  if (state.filters.roomFeatures.value?.length) {
    const options = [
      ...searchOptions.roomFeatures,
      ...searchOptions.allowedEvents,
    ];
    state.filters.roomFeatures.value.forEach(value => {
      const option = options.find(opt => parseInt(opt.id, 10) === parseInt(value, 10));

      if (option?.label) {
        arr.push({ label: option.label, disabled: isFilterReduced('roomFeatures', value.toString()) });
      }
    });
  }

  // Venue features
  if (state.filters.venueFeatures.value?.length) {
    state.filters.venueFeatures.value.forEach(value => {
      const option = searchOptions.venueFeatures.find(opt => parseInt(opt.id, 10) === parseInt(value, 10));
      if (option?.label) {
        arr.push({ label: option.label, ...getFilterReducedProperties('venueFeatures', value) });
      }
    });
  }

  // Allowed events
  if (state.filters.allowedEvents.value?.length) {
    state.filters.allowedEvents.value.forEach(value => {
      const option = searchOptions.allowedEvents.find(opt => parseInt(opt.id, 10) === parseInt(value, 10));

      if (option?.label) {
        arr.push({ label: option.label, disabled: isFilterReduced('allowedEvents', value.toString()) });
      }
    });
  }

  // Accessibility features
  if (state.filters.accessibilityFeatures.value) {
    Object.keys(state.filters.accessibilityFeatures.value).forEach(value => {
      if (state.filters.accessibilityFeatures.value[value]) {
        const option = searchOptions.accessibilityFeatures.find(opt => opt.id === value);
        if (option?.label) {
          arr.push({ label: option.label, disabled: false });
        }
      }
    });
  }

  // Venue parts
  if (state.filters.venueParts.value?.length) {
    state.filters.venueParts.value.forEach(value => {
      const option = state.venuePartsList.find(opt => opt.value === value);
      if (option?.filter_label) {
        arr.push({ label: option.filter_label, ...getFilterReducedProperties('venueParts') });
      }
    });
  }

  // Venue types
  if (state.filters.venueTypes.value?.length) {
    const venueTypes = state.venueTypesPerEventCategoryConfig?.[state.eventCategory];
    if (venueTypes) {
      state.filters.venueTypes.value.forEach(value => {
        const option = venueTypes.find(opt => parseInt(opt.id, 10) === parseInt(value, 10));
        if (option?.label) {
          arr.push({ label: option.label, ...getFilterReducedProperties('venueTypes') });
        }
      });
    }
  }

  // Boolean filters
  if (state.filters.flexibleCancellation.value) {
    arr.push({ label: 'Cancellation flexibility', ...getFilterReducedProperties('flexibleCancellation') });
  }

  if (state.filters.superVenuesOnly.value) {
    arr.push({ label: 'Only show supervenues', ...getFilterReducedProperties('superVenuesOnly') });
  }

  if (state.filters.noAgeRestriction.value) {
    arr.push({ label: 'No age restriction', disabled: false });
  }

  // Remove duplicates based on label
  const uniqueArr = arr.filter((item, index, self) => index === self.findIndex((t) => t.label === item.label));

  // Sort the array by disabled property
  return uniqueArr.sort((a, b) => a.disabled - b.disabled);
}
